import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  type ConsensusReconciliationSseEventPayload,
  ReconciliationOutcome,
} from '@api-types/sse/event-payloads/consensus-reconciliation.sse-event-payload';
import { scopeIntersectsFilter } from '@api-types/sse/reconciliation-scope';
import { SseEventType } from '@api-types/sse/sse.types';
import { SseEventHandler } from '@core/sse/sse-event-handler';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { buffer, debounceTime, Subject } from 'rxjs';
import { selectConsensusViewerFilter } from '../filter/filter.selectors';
import { selectSelectedConsensusTemplateId } from '../router-state/router.selectors';
import { ConsensusGridStateService } from './consensus-grid-state.service';
import { ConsensusPlannerNamesService } from './consensus-planner-names.service';
import { ConsensusValuesStateService } from './consensus-values-state.service/consensus-values-state.service';

export const REFRESH_COALESCE_MS = 250;

@Injectable()
export class ConsensusReconciliationSseHandler implements SseEventHandler<
  typeof SseEventType.ConsensusReconciliation
> {
  readonly eventType = SseEventType.ConsensusReconciliation;

  private readonly consensusGridStateService = inject(
    ConsensusGridStateService,
  );
  private readonly consensusValuesStateService = inject(
    ConsensusValuesStateService,
  );
  private readonly plannerNames = inject(ConsensusPlannerNamesService);
  private readonly toast = inject(ToastrService);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly selectedTemplateId = this.store.selectSignal(
    selectSelectedConsensusTemplateId,
  );
  private readonly viewerFilter = this.store.selectSignal(
    selectConsensusViewerFilter,
  );

  private readonly completed =
    new Subject<ConsensusReconciliationSseEventPayload>();

  constructor() {
    this.completed
      .pipe(
        buffer(this.completed.pipe(debounceTime(REFRESH_COALESCE_MS))),
        takeUntilDestroyed(inject(DestroyRef)),
      )
      .subscribe((batch) => {
        this.notifyOtherPlannersEdits(batch);
        void this.consensusGridStateService.refreshAfterReconciliation(
          batch.map(({ scope, consensusColumnId, dateValue }) => ({
            scope,
            consensusColumnId,
            dateValue,
          })),
        );
      });
  }

  handle(payload: ConsensusReconciliationSseEventPayload): void {
    if (payload.consensusTemplateId !== this.selectedTemplateId()) return;
    const inView = scopeIntersectsFilter(payload.scope, this.viewerFilter());

    if (payload.isReconciling) {
      if (!inView) return;
      this.consensusGridStateService.addReconcilingScope(
        payload.scope,
        payload.consensusColumnId,
        payload.dateValue,
      );
      return;
    }

    this.notifyOwnOutcome(payload);
    if (!inView) {
      this.consensusGridStateService.dropReconcilingScope(
        payload.scope,
        payload.consensusColumnId,
        payload.dateValue,
      );
      return;
    }
    this.consensusGridStateService.markScopeCompletedAwaitingRefresh(
      payload.scope,
      payload.consensusColumnId,
      payload.dateValue,
    );
    this.consensusValuesStateService.evictMonths(
      [payload.dateValue],
      [payload.scope],
    );
    this.completed.next(payload);
  }

  /** The planner's own edit did not land: say so, the grid shows the old value again. */
  private notifyOwnOutcome(
    payload: ConsensusReconciliationSseEventPayload,
  ): void {
    if (!this.plannerNames.isCurrentUser(payload.userId)) return;
    if (payload.outcome === ReconciliationOutcome.Superseded) {
      this.toast.warning(
        this.translate.instant('CONSENSUS.EDIT_SUPERSEDED_MESSAGE', {
          month: payload.dateValue,
        }),
        this.translate.instant('CONSENSUS.EDIT_SUPERSEDED_TITLE'),
      );
    } else if (payload.outcome === ReconciliationOutcome.Failed) {
      this.toast.error(this.translate.instant('CONSENSUS.SAVE_FAILED'));
    }
  }

  /**
   * One non-blocking notice per planner whose finished edit touches a row
   * this grid renders. The values refresh in place either way; the notice
   * only says why they moved.
   */
  private notifyOtherPlannersEdits(
    batch: ConsensusReconciliationSseEventPayload[],
  ): void {
    const monthsByPlanner = new Map<string, Set<string>>();
    for (const payload of batch) {
      if (payload.outcome !== ReconciliationOutcome.Completed) continue;
      if (this.plannerNames.isCurrentUser(payload.userId)) continue;
      if (
        !this.consensusGridStateService.hasRenderedRowInScope([payload.scope])
      ) {
        continue;
      }
      const months = monthsByPlanner.get(payload.userId) ?? new Set();
      months.add(payload.dateValue);
      monthsByPlanner.set(payload.userId, months);
    }
    for (const [userId, months] of monthsByPlanner) {
      this.toast.info(
        this.translate.instant('CONSENSUS.VALUES_UPDATED_BY_OTHER_MESSAGE', {
          user: this.plannerNames.labelFor(userId),
          months: [...months].sort((a, b) => a.localeCompare(b)).join(', '),
        }),
        this.translate.instant('CONSENSUS.VALUES_UPDATED_BY_OTHER_TITLE'),
      );
    }
  }
}
